import axios from "axios";
import {
  GET_PRINTERS,
  GET_ERROR,
  DISABLE_PRINTER,
  EDIT_PRINTER,
} from "./types";
import setAuthToken from "../utils/setAuthToken";
import { toastr } from "react-redux-toastr";
import { logout } from "./auth";

//  Get printer
export const getPrinters = () => async (dispatch) => {
  try {
    const res = await axios.get("/api/printer");
    dispatch({
      type: GET_PRINTERS,
      payload: res.data,
    });
  } catch (err) {
    // No response: the network or the server is down. The next refresh tries again.
    if (!err.response) return;
    if (
      err.response.status === 401 &&
      err.response.data.msg === "Token is not valid"
    ) {
      window.localStorage.clear("x-auth-token");
      toastr.error("Session expired");
      dispatch(logout());
    }
    const errors = err.response.data.errors;
    if (errors) {
      errors.forEach((error) => toastr.error(error.msg));
    }
  }
};

// Each kiosk's ink as the backend estimates it from the marks recorded at it: { marks,
// printers: [{ id, black, color }] }, or null when it failed. Quiet: getPrinters,
// which refreshes more often, shows what is wrong.
export const getInk = () => async () => {
  try {
    const res = await axios.get("/api/printer/ink");
    // A backend without this route answers with the admin's index.html and a 200
    return res.data && Array.isArray(res.data.printers) ? res.data : null;
  } catch (err) {
    return null;
  }
};

// Disable printer
export const disablePrinter = (printerId) => async (dispatch) => {
  const config = {
    headers: {
      "Content-Type": "application/json",
    },
  };

  const body = JSON.stringify({ printerId });

  try {
    const res = await axios.put("/api/printer/disable", body, config);
    dispatch({
      type: DISABLE_PRINTER,
      payload: res.data,
    });
    toastr.success("Printer has been disabled");
  } catch (err) {
    if (
      err.response.status === 401 &&
      err.response.data.msg === "Token is not valid"
    ) {
      window.localStorage.clear("x-auth-token");
      toastr.error("Session expired");
      dispatch(logout());
    }
    const errors = err.response.data.errors;
    if (errors) {
      errors.forEach((error) => toastr.error(error.msg));
    }
  }
};

// Resolve a printer's error, or enable a disabled printer: both set it back to running
export const setPrinterRunning = (printerId, message) => async (dispatch) => {
  const config = {
    headers: {
      "Content-Type": "application/json",
    },
  };

  // "" leaves a field unchanged.
  const body = JSON.stringify({
    printerId,
    location: "",
    status: "running",
    error: "none",
    paper: "",
    paperDate: "",
    phoneNumber: "",
  });

  try {
    const res = await axios.put("/api/printer", body, config);
    dispatch({
      type: EDIT_PRINTER,
      payload: res.data,
    });
    toastr.success(message);
  } catch (err) {
    if (
      err.response.status === 401 &&
      err.response.data.msg === "Token is not valid"
    ) {
      window.localStorage.clear("x-auth-token");
      toastr.error("Session expired");
      dispatch(logout());
    }
    const errors = err.response.data.errors;
    if (errors) {
      errors.forEach((error) => toastr.error(error.msg));
    }
  }
};

// Edit printer. fields holds only what the edit form changed, named as PUT
// /api/printer takes them, but for heartbeatMinutes. The status is changed only by
// setPrinterRunning and disablePrinter.
export const editPrinter =
  (printerId, { heartbeatMinutes, ...fields }) =>
  async (dispatch) => {
    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };

    const body = JSON.stringify({
      printerId,
      // "" leaves these unchanged, and null empties the location or phone number. The
      // rear tray, photo, place and ink fields are unchanged when left out. A paper
      // count or ink marks are saved as a service visit, which dates the count.
      printerName: "",
      location: "",
      status: "",
      paper: "",
      phoneNumber: "",
      error: "",
      ...fields,
      // The backend stores seconds; null goes back to its default interval.
      ...(heartbeatMinutes !== undefined && {
        heartbeatIntervalSec:
          heartbeatMinutes === null
            ? null
            : Math.round(Number(heartbeatMinutes) * 60),
      }),
    });

    try {
      const res = await axios.put("/api/printer", body, config);
      dispatch({
        type: EDIT_PRINTER,
        payload: res.data,
      });
      toastr.success("Printer has been edited");
      // Tells the edit form whether it can close
      return true;
    } catch (err) {
      if (
        err.response.status === 401 &&
        err.response.data.msg === "Token is not valid"
      ) {
        window.localStorage.clear("x-auth-token");
        toastr.error("Session expired");
        dispatch(logout());
      }
      const errors = err.response.data.errors;
      if (errors) {
        errors.forEach((error) => toastr.error(error.msg));
      }
      return false;
    }
  };
